//add-product-page.tsx

import { Button } from "@mui/material";
import { useEffect, useState, useContext } from "react";
import { useHistory, useLocation } from "react-router-dom";
import { sizesApi } from "../services/api/sizes";
import { cropsApi } from "../services/api/crops";
import { Crop } from "../models/crop";
import { Size } from "../models/size";
import Page from "../components/page";
import LoadingBackdrop from "../components/loading-backdrop";
import { productsApi, ProductTypes } from "../services/api/products";
import ProductForm, { ProductFormValues } from "../components/product-form";
import { UserContext, ErrorContext } from "../components/app-routes";
import { formatCurrency, toCurrency } from "./currency";
import { DateTime } from "luxon";
import mixpanel from "mixpanel-browser";
import { goBackOrTo } from "../utils/history";
import { Product } from "../models/product";
import StickyActionBar from "../components/sticky-action-bar";
import { Pkg } from "../models/pkg";
import { pkgsApi } from "../services/api/pkgs";
import { getCustomLabel } from "../utils/custom-labels";

interface Data {
  crops: Crop[];
  productSizes: Size[];
  pkgs: Pkg[];
}

export default function AddProductPage() {
  const history = useHistory();
  const [data, setData] = useState<Data | null>(null);
  const [busy, setBusy] = useState(false);
  const [wasSaved, setWasSaved] = useState(false);
  const { grower } = useContext(UserContext);
  const { handleError } = useContext(ErrorContext);
  const { state } = useLocation<{ product: Product }>();

  if (!grower) {
    throw new Error("Invalid UserContext");
  }

  const initialValues = state
    ? {
        name: state.product.name,
        productCrops: state.product.productCrops.map((c) => ({
          id: 0,
          cropId: c.crop.id.toString(),
          blend: c.blend.toString(),
        })),
        productSizes: state.product.productSizes.map((pS) => ({
          id: 0,
          type: pS.type,
          price: formatCurrency(pS.price, grower.CountryISO, {
            symbol: "",
            separator: "",
          }),
          weight: pS.weight ? pS.weight?.toString() : undefined,
          sizeId: pS.size?.id.toString(),
          pkgId: pS.pkgId?.toString() || "",
        })),
        notes: state.product.notes,
        archived: state.product.archivedAt !== null,
      }
    : {
        name: "",
        productCrops: [
          {
            id: 0,
            cropId: "",
            blend: "100",
          },
        ],
        productSizes: [
          {
            type: ProductTypes.Cut,
            sizeId: "",
            weight: "",
            pkgId: "",
            price: "",
          },
        ],
        notes: "",
        archived: false,
      };

  const isPro = grower.Plan === "pro";

  useEffect(() => {
    const isMounted = { current: true };

    async function load() {
      setBusy(true);
      try {
        const productSizes = await sizesApi.getAll();
        const crops = await cropsApi.getAll();
        const pkgs = isPro ? await pkgsApi.getAll() : [];

        if (isMounted.current) {
          setData({
            crops,
            productSizes,
            pkgs,
          });
        }
      } catch (error) {
        if (isMounted.current) handleError(error);
      } finally {
        if (isMounted.current) setBusy(false);
      }
    }

    load();

    return () => {
      isMounted.current = false;
    };
  }, []);

  const handleSubmit = async (values: ProductFormValues) => {
    setBusy(true);

    try {
      await productsApi.add({
        id: 0,
        name: values.name,
        productCrops: values.productCrops.map((c) => ({
          id: 0,
          crop: null as unknown as Crop,
          cropId: parseInt(c.cropId),
          blend: parseFloat(c.blend),
        })),
        productSizes: values.productSizes.map((pS) => ({
          id: 0,
          type: pS.type as ProductTypes,
          weight: pS.weight ? parseFloat(pS.weight) : undefined,
          sizeId: pS.sizeId ? parseInt(pS.sizeId) : undefined,
          size: null as unknown as Size,
          pkgId: pS.pkgId ? parseInt(pS.pkgId) : undefined,
          price: toCurrency(pS.price, grower.CountryISO).intValue,
        })),
        notes: values.notes,
        archivedAt: values.archived ? DateTime.now().toISO() : null,
      });

      setWasSaved(true);

      mixpanel.track("Add Product");
    } catch (error) {
      handleError(error);
    } finally {
      setBusy(false);
    }
  };

  useEffect(() => {
    if (wasSaved) {
      history.replace("/home/products");
    }
  }, [wasSaved, history]);

  return (
    <Page title={`Add ${getCustomLabel(grower ?? null, "products")}`}>
      {data && (
        <ProductForm
          initialValues={initialValues}
          onSubmit={handleSubmit}
          crops={data.crops}
          sizes={data.productSizes}
          pkgs={data.pkgs}
          saved={wasSaved}
        >
          <StickyActionBar position="bottom" sx={{ px: 0 }}>
            <Button
              variant="outlined"
              disabled={busy}
              onClick={() => goBackOrTo(history, "/home/products")}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              color="primary"
              type="submit"
              disabled={busy}
            >
              Add
            </Button>
          </StickyActionBar>
        </ProductForm>
      )}
      <LoadingBackdrop open={busy} />
    </Page>
  );
}
